import {
  type DesignDocument,
  type guestQuoteRequestFormSchema,
  guestQuoteRequestInputSchema,
} from '@repo/api-contracts';
import { useState } from 'react';
import type { z } from 'zod';
import { trpc } from '@/shared/api';
import { getOrCreateGuestKey } from '@/shared/lib';
import {
  type GuestQuoteRequestCommonValues,
  type GuestQuoteRequestDestinationErrors,
  type GuestQuoteRequestFieldErrors,
  type GuestQuoteRequestReceipt,
  type GuestQuoteRequestSubmissionErrors,
  guestQuoteRequestSubmitErrors,
  realClock,
  toGuestQuoteRequestCommonInput,
  type UseSubmitGuestQuoteRequestOptions,
  type UseSubmitGuestQuoteRequestResult,
  validateGuestQuoteRequest,
} from './guest-quote-request-submission';

export type {
  GuestDestinationValues,
  GuestQuoteRequestDestinationErrors,
  GuestQuoteRequestFieldErrors,
  GuestQuoteRequestFieldKey,
  GuestQuoteRequestReceipt,
} from './guest-quote-request-submission';

/** 비회원 견적 요청 정보(quotes.getGuestRequestForm 응답) — 회원 요청 정보와 같은 칸 타입(디자인 id·기본 담당자명·썸네일 없음). */
export type GuestQuoteRequestFormData = z.infer<typeof guestQuoteRequestFormSchema>;

/** 검증 전 폼 입력 — 수량·날짜·연락처도 입력창 문자열 그대로 받고 검증은 safeParse 가 한다. 견적 수량은 배송지 수량 합계다. */
export interface GuestQuoteRequestFormValues extends GuestQuoteRequestCommonValues {
  /** 에디터 임시 작업 — 요청 순간 서버가 검증·고정한다(설계 G5). */
  readonly design: DesignDocument;
  readonly printMethodId: string;
}

/** 입력창 문자열을 계약 입력 모양으로 — 디자인 문서·인쇄 방식 + 공용 칸. */
const toInput = (values: GuestQuoteRequestFormValues, guestKey: string): unknown => ({
  ...toGuestQuoteRequestCommonInput(values, guestKey),
  design: values.design,
  printMethodId: values.printMethodId,
});

/**
 * 비회원 견적 요청 제출(quotes.submitGuestRequest, 비회원 견적 설계 G5·G12·G16).
 * 계약 스키마와 회원 요청과 같은 규칙(최소 수량·견적 금액 상한·한국 내일 이후)으로 선검증 → 칸별 오류. 연락처는 고른 방법 하나만
 * 싣고(이메일은 소문자, 휴대폰은 숫자만 — 계약 스키마가 정규화한다), 비회원 키와 에디터 임시 작업(디자인 문서)을 함께 보낸다.
 * 한도 초과(TOO_MANY_REQUESTS)는 "잠시 후 다시 시도해 주세요.", 그 밖의 서버 거절은 서버 문구를 폼 오류로 보여 준다.
 */
export const useSubmitGuestQuoteRequest = ({
  minOrderQty,
  priceTiers,
  shippingFeeSettings,
  sizeOptions,
  now = realClock,
  readGuestKey = getOrCreateGuestKey,
}: UseSubmitGuestQuoteRequestOptions): UseSubmitGuestQuoteRequestResult<GuestQuoteRequestFormValues> => {
  const [fieldErrors, setFieldErrors] = useState<GuestQuoteRequestFieldErrors>({});
  const [destinationErrors, setDestinationErrors] = useState<GuestQuoteRequestDestinationErrors>(
    {},
  );
  const [formError, setFormError] = useState<string | null>(null);
  const mutation = trpc.quotes.submitGuestRequest.useMutation();

  const show = (errors: GuestQuoteRequestSubmissionErrors): void => {
    setFieldErrors(errors.fieldErrors);
    setDestinationErrors(errors.destinationErrors);
    setFormError(errors.formError);
  };

  const submit = async (
    values: GuestQuoteRequestFormValues,
  ): Promise<GuestQuoteRequestReceipt | null> => {
    const validation = validateGuestQuoteRequest(
      guestQuoteRequestInputSchema.safeParse(toInput(values, readGuestKey())),
      values,
      { minOrderQty, priceTiers, now: now() },
      shippingFeeSettings,
      undefined,
      sizeOptions,
    );
    show(validation);

    if (validation.input === null) {
      return null;
    }

    try {
      const result = await mutation.mutateAsync(validation.input);
      return { ...result, contactMethod: values.contactMethod };
    } catch (error) {
      show(guestQuoteRequestSubmitErrors(error, values));
      return null;
    }
  };

  return { fieldErrors, destinationErrors, formError, isPending: mutation.isPending, submit };
};
